import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import axios from "axios";

// NASA color palette
const NASA_COLORS = [
    "#00FFFF", "#32CD32", "#1E90FF", "#00FA9A", "#7FFFD4",
    "#20B2AA", "#4682B4", "#00CED1", "#5F9EA0", "#00BFFF",
];

// Endpoints
const ENDPOINT_PAYROLL_SUMMARY = "https://figazuresql.swancreate.com/api/swa/vw_PayrollSummaryPerSite";
const ENDPOINT_WEEKLY_HOURS = "https://figazuresql.swancreate.com/api/swa/vw_WeeklyHoursPerUser";
const ENDPOINT_TIMECARD = "https://figazuresql.swancreate.com/api/swa/TimeCard";

// Currency helper
const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

export default function PayrollDashboard() {
    const [weeklyHours, setWeeklyHours] = useState([]);
    const [timeCardData, setTimeCardData] = useState([]);

    // Filters
    const [selectedSite, setSelectedSite] = useState("All");
    const [selectedMonth, setSelectedMonth] = useState("All");
    const [selectedWeek, setSelectedWeek] = useState("All");

    // Filtered timecards
    const [filteredTimeCard, setFilteredTimeCard] = useState([]);

    // Fetch on mount
    useEffect(() => {
        (async () => {
            try {
                const [weeklyRes, tcRes] = await Promise.all([
                    axios.get(ENDPOINT_WEEKLY_HOURS),
                    axios.get(ENDPOINT_TIMECARD),
                ]);
                setWeeklyHours(
                    weeklyRes.data.map(r => ({
                        ...r,
                        WeekStartDate: new Date(r.WeekStartDate),
                        WeekEndDate: new Date(r.WeekEndDate),
                        TotalPayEarned: parseFloat(r.TotalPayEarned) || 0,
                    }))
                );
                setTimeCardData(
                    tcRes.data.map(r => ({
                        ...r,
                        ClockInTime: r.ClockInTime ? new Date(r.ClockInTime) : null,
                        ClockOutTime: r.ClockOutTime ? new Date(r.ClockOutTime) : null,
                        TotalPay: parseFloat(r.TotalPay) || 0,
                    }))
                );
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    // Apply filters
    useEffect(() => {
        let ft = [...timeCardData];
        if (selectedSite !== "All") {
            ft = ft.filter(r => r.SiteID === selectedSite);
        }
        if (selectedMonth !== "All") {
            ft = ft.filter(r =>
                r.ClockInTime && r.ClockInTime.getMonth() + 1 === +selectedMonth
            );
        }
        if (selectedWeek !== "All") {
            const start = new Date(selectedWeek);
            const end = new Date(start);
            end.setDate(end.getDate() + 6);
            ft = ft.filter(r =>
                r.ClockInTime && r.ClockInTime >= start && r.ClockInTime <= end
            );
        }
        setFilteredTimeCard(ft);
    }, [timeCardData, selectedSite, selectedMonth, selectedWeek]);

    // Fiscal year July 1 → June 30
    const today = new Date();
    const fyStartYear = today.getMonth() >= 6
        ? today.getFullYear()
        : today.getFullYear() - 1;
    const fiscalYearStart = new Date(fyStartYear, 6, 1);
    const fiscalYearEnd = new Date(fyStartYear + 1, 5, 30);

    // Build chart data
    const fyWeeks = weeklyHours.filter(w =>
        w.WeekStartDate >= fiscalYearStart && w.WeekStartDate <= fiscalYearEnd
    );
    const grouped = {};
    fyWeeks.forEach(w => {
        const iso = w.WeekStartDate.toISOString().slice(0, 10);
        if (!grouped[iso]) grouped[iso] = {};
        const site = w.SiteName || "Unknown";
        grouped[iso][site] = (grouped[iso][site] || 0) + w.TotalPayEarned;
    });
    const multiLineData = Object.keys(grouped)
        .sort()
        .map(week => ({ week, ...grouped[week] }));

    // Site names for lines
    const siteNames = Array.from(
        multiLineData.reduce((s, row) => {
            Object.keys(row).forEach(k => {
                if (k !== "week") s.add(k);
            });
            return s;
        }, new Set())
    );

    // Distinct filter lists
    const allSites = Array.from(new Set(timeCardData.map(r => r.SiteID)));
    const allWeeks = Array.from(new Set(
        weeklyHours.map(w => w.WeekStartDate.toISOString().slice(0, 10))
    )).sort();

    const handleExportCSV = () => {
        if (!filteredTimeCard.length) {
            alert("No data to export.");
            return;
        }
        const headers = [
            "TimeCardID", "UserID", "EmployeeName", "SiteID", "SiteName",
            "ClockInTime", "ClockOutTime", "TimeWorked", "HourlyRate", "TotalPay",
            "ERS", "WorkersComp", "FICA", "apiid"
        ];
        const rows = filteredTimeCard.map(r => {
            const t = r.TotalPay;
            return [
                r.TimeCardID, r.UserID, r.EmployeeName, r.SiteID, r.SiteName,
                r.ClockInTime?.toISOString() || "",
                r.ClockOutTime?.toISOString() || "",
                r.TimeWorked, r.HourlyRate,
                formatCurrency(t),
                formatCurrency(t * 0.152),
                formatCurrency(t * 0.0547),
                formatCurrency(t * 0.0765),
                r.apiid
            ];
        });
        let csv = "data:text/csv;charset=utf-8," +
            headers.join(",") + "\n" +
            rows.map(r => r.join(",")).join("\n");
        const link = document.createElement("a");
        link.href = encodeURI(csv);
        link.download = "timecard_export.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box
            m="20px"
            sx={{
                backgroundColor: "#121212",
                color: "#EFFFFD",
                borderRadius: "10px",
                p: "20px",
            }}
        >
            <Typography variant="h4" fontWeight="bold" textAlign="center" mb={3}>
                Payroll & Hours Dashboard
            </Typography>

            {/* Filters */}
            <Box display="flex" gap={3} mb={4} justifyContent="flex-end">
                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Site</InputLabel>
                    <Select
                        value={selectedSite}
                        onChange={e => setSelectedSite(e.target.value)}
                        sx={{ color: "#EFFFFD" }}
                    >
                        <MenuItem value="All">All</MenuItem>
                        {allSites.map(id => {
                            const rec = timeCardData.find(r => r.SiteID === id);
                            return (
                                <MenuItem key={id} value={id}>
                                    {rec?.SiteName || id}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                        value={selectedMonth}
                        onChange={e => setSelectedMonth(e.target.value)}
                        sx={{ color: "#EFFFFD" }}
                    >
                        <MenuItem value="All">All</MenuItem>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                            <MenuItem key={m} value={m}>
                                {new Date(0, m - 1).toLocaleString("default", { month: "long" })}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Week</InputLabel>
                    <Select
                        value={selectedWeek}
                        onChange={e => setSelectedWeek(e.target.value)}
                        sx={{ color: "#EFFFFD" }}
                    >
                        <MenuItem value="All">All</MenuItem>
                        {allWeeks.map(iso => {
                            const w = weeklyHours.find(w => w.WeekStartDate.toISOString().slice(0, 10) === iso);
                            if (!w) return null;
                            return (
                                <MenuItem key={iso} value={iso}>
                                    {new Date(w.WeekStartDate).toLocaleDateString()} –{" "}
                                    {new Date(w.WeekEndDate).toLocaleDateString()}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    onClick={handleExportCSV}
                    sx={{ backgroundColor: "#1E90FF", color: "#121212" }}
                >
                    Export TimeCard to CSV
                </Button>
            </Box>

            {/* Updated Line Chart: Full FY July 1 – June 30 */}
            <Box mb={4} sx={{ backgroundColor: "#1A1A2E", p: 2, borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="600" mb={2}>
                    Weekly Payroll Costs (FY {fyStartYear}–{fyStartYear + 1})
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={multiLineData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#00FFFF" />
                        <XAxis dataKey="week" stroke="#EFFFFD" />
                        <YAxis stroke="#EFFFFD" />
                        <Tooltip formatter={val => `$${(val || 0).toFixed(2)}`} />
                        <Legend />
                        {siteNames.map((site, idx) => (
                            <Line
                                key={site}
                                type="monotone"
                                dataKey={site}
                                stroke={NASA_COLORS[idx % NASA_COLORS.length]}
                                strokeWidth={2}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </Box>

            {/* TimeCard Table */}
            <Paper sx={{ p: 2, backgroundColor: "#1A1A2E", borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="600" mb={2}>
                    Time Card Records
                </Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: "#EFFFFD" }}>Employee</TableCell>
                                <TableCell sx={{ color: "#EFFFFD" }}>Site</TableCell>
                                <TableCell sx={{ color: "#EFFFFD" }}>Clock In</TableCell>
                                <TableCell sx={{ color: "#EFFFFD" }}>Clock Out</TableCell>
                                <TableCell sx={{ color: "#EFFFFD" }}>Hours</TableCell>
                                <TableCell sx={{ color: "#EFFFFD" }}>Total Pay</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTimeCard.map(tc => (
                                <TableRow key={tc.TimeCardID}>
                                    <TableCell sx={{ color: "#EFFFFD" }}>{tc.EmployeeName}</TableCell>
                                    <TableCell sx={{ color: "#EFFFFD" }}>{tc.SiteName}</TableCell>
                                    <TableCell sx={{ color: "#EFFFFD" }}>
                                        {tc.ClockInTime?.toLocaleString() || ""}
                                    </TableCell>
                                    <TableCell sx={{ color: "#EFFFFD" }}>
                                        {tc.ClockOutTime?.toLocaleString() || ""}
                                    </TableCell>
                                    <TableCell sx={{ color: "#EFFFFD" }}>
                                        {tc.TimeWorked?.toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ color: "#EFFFFD" }}>
                                        {formatCurrency(tc.TotalPay)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
}
